/**
 * Document History part of Account Settings. History is written by the
 * SharePoint connection, so without it the tab offers to connect (the same
 * access screen as Integrations) instead of only opening an empty page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { FiFileText } from 'react-icons/fi';

import { DataState } from '../../context/DataProvider';
import { useTeams } from '../../context/TeamsProvider';
import { useAccess } from '../../services/access';
import { getAuthenticatedApps } from '../../services/api';
import { authenticateApp } from '../../utils/auth';
import SharePointAccessModal from './SharePointAccessModal';

const DocumentHistorySettingsContent = ({ onOpen }) => {
  const { userGoogleId } = DataState();
  const { isTeamsInitialized } = useTeams();
  const canConnect = useAccess().can('integration:connect');
  const [connected, setConnected] = useState(null); // null = checking
  const [choosing, setChoosing] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const check = useCallback(async () => {
    try {
      const result = await getAuthenticatedApps(userGoogleId);
      setConnected((result?.apps || []).includes('microsoft_sharepoint'));
    } catch (error) {
      setConnected(false);
    }
  }, [userGoogleId]);

  useEffect(() => {
    check();
  }, [check]);

  const connect = (sharepointAccess) => {
    setChoosing(false);
    setConnecting(true);
    authenticateApp(
      'microsoft_sharepoint',
      userGoogleId,
      () => {},
      () => {},
      isTeamsInitialized || false,
      {
        sharepointAccess,
        // Success, error, closed or blocked popup: never leave a spinner.
        onDone: async () => {
          await check();
          setConnecting(false);
        },
      },
    );
  };

  const button =
    'mt-4 rounded-lg px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50';

  return (
    <div>
      <h2 className="mb-3 text-lg font-semibold text-slate-100">
        Document History
      </h2>
      <div className="rounded-xl border border-slate-700/70 bg-slate-800/70 p-4">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-blue-600/15 p-2 text-blue-300">
            <FiFileText className="text-lg" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-100">
              SharePoint document change audit
            </p>
            <p className="mt-1 text-xs leading-5 text-slate-400">
              View which SharePoint document was updated, what content changed,
              who performed the update, and when it happened.
            </p>
            {connected === null ? (
              <p className="mt-4 text-xs text-slate-400">
                Checking SharePoint…
              </p>
            ) : connected ? (
              <button
                type="button"
                onClick={onOpen}
                className={`${button} bg-[#5652CF] text-white hover:bg-[#4642bf]`}
              >
                Open Document History
              </button>
            ) : (
              <>
                <p className="mt-3 text-xs text-amber-300">
                  SharePoint is not connected, so new changes aren't recorded.
                </p>
                <div className="flex flex-wrap gap-2">
                  {canConnect && (
                    <button
                      type="button"
                      onClick={() => setChoosing(true)}
                      disabled={connecting}
                      className={`${button} bg-[#5652CF] text-white hover:bg-[#4642bf]`}
                    >
                      {connecting ? 'Connecting…' : 'Connect SharePoint'}
                    </button>
                  )}
                  {/* Changes recorded before a disconnect stay readable. */}
                  <button
                    type="button"
                    onClick={onOpen}
                    className={`${button} border border-slate-600 text-slate-200 hover:bg-slate-700/60`}
                  >
                    View past changes
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {choosing && (
        <SharePointAccessModal
          onContinue={connect}
          onClose={() => setChoosing(false)}
        />
      )}
    </div>
  );
};

export default DocumentHistorySettingsContent;
